import { inject, Injectable } from '@angular/core';
import { DEFAULT_LAYER_STYLES } from '@cloud-twin-v2/ct-map/models/default-layer-styles';
import { LayerType } from '@cloud-twin-v2/generated/simulation.generated.client';
import { ArrayHelperService } from '@common-modules/shared/helpers/array-helper.service';
import { globalUtilsHelper } from '@common-modules/shared/helpers/global-utils-helper';
import { Leaflet } from '@common-modules/wlm-map/leaflet-export';
import { LayerItemV2 } from '../models/layer-item';
import {
  MapCssDefinition,
  MapCssNativeDefinition,
  MapCssStyle,
  MapThematicStyle,
} from '../models/layer-type';
import { MapGenericStyle } from '../models/map-generic-style';
import { NativeLayer } from '../models/native-layer';
import { GenericMapDataService } from './generic-map-data.service';

export const ICON_DEFAULT_SIZE = 30;

@Injectable()
export class GenericMapStylesService {
  private readonly _arrayHelper = inject(ArrayHelperService);
  private readonly _mapDataService = inject(GenericMapDataService);

  getCSSStyle = (style: MapGenericStyle) => style.styleDefinition as MapCssStyle;
  getRangeDefinition = (style: MapGenericStyle) =>
    (style.styleDefinition as MapThematicStyle)?.rangeDefinition;
  isRangeStyle = (style) => style.styleDefinition && !!style.styleDefinition.rangeDefinition;

  setStyledIcon(layerItem: LayerItemV2): void {
    if (
      !layerItem.iconPath ||
      !layerItem.layerStyle ||
      layerItem.layerStyle.isThematic ||
      layerItem.layerDefinition.layerType !== LayerType.Point
    ) {
      return;
    }

    const cssStyle = this.getCSSStyle(layerItem.layerStyle);
    const updatedSvgIcon = this.applyStyleToIcon(layerItem.iconPath, cssStyle.definition);
    layerItem.iconPath = updatedSvgIcon;
  }

  applyStyleToIcon(svgIcon: string, cssDefinition: MapCssDefinition): string {
    let updatedSvgIcon = svgIcon;
    const color = cssDefinition?.Fill ?? cssDefinition?.Color;
    if (color) {
      updatedSvgIcon = globalUtilsHelper.applySvgColor(updatedSvgIcon, color);
    }
    return updatedSvgIcon;
  }

  getStylesFn(
    style: MapGenericStyle,
    layerItem: LayerItemV2,
    getLayerId: (innerLayer: NativeLayer) => string,
  ): (nativeLayer: NativeLayer) => MapCssStyle {
    return (nativeLayer: NativeLayer) => {
      if (!this.isRangeStyle(style)) {
        const cssStyle = this.getCSSStyle(style);
        return cssStyle;
      }

      const styleDefinition = style.styleDefinition as MapThematicStyle;
      const rangeDefinition = this.getRangeDefinition(style);

      if (!rangeDefinition || !rangeDefinition.length) {
        return { definition: {} } as MapCssStyle;
      }

      const features = nativeLayer.feature;
      const extendedProperties = this._mapDataService.getProperties(nativeLayer, getLayerId);
      const { fieldName } = styleDefinition;
      const currentValue = features.properties[fieldName] ?? extendedProperties[fieldName];

      if (currentValue == null || typeof currentValue === 'undefined') {
        return this.getDefaultPointStyles();
      }

      const ranges = this._arrayHelper.sortObjects(rangeDefinition, (s) => s.value);

      if (ranges.length === 1) {
        return ranges[0].styleDefinition;
      }

      for (let i = 0; i < ranges.length; i++) {
        const curr = ranges[i];
        if (currentValue < curr.value) {
          return ranges[i].styleDefinition;
        }
      }
      return ranges[ranges.length - 1].styleDefinition;
    };
  }

  buildIcon(iconPath: string, styles: MapCssStyle): any {
    const size = ICON_DEFAULT_SIZE;
    return Leaflet.divIcon({
      html: this.applyStyleToIcon(iconPath, styles.definition),
      iconSize: [size, size],
      iconAnchor: [size / 2, size / 2],
      popupAnchor: [0, -size / 2],
    });
  }

  apiToNativeStyles(definition: MapCssDefinition): MapCssNativeDefinition {
    if (!definition) {
      return null;
    }

    const nativeDefinition: MapCssNativeDefinition = {
      ...definition,
      color: definition.Color ?? definition.Fill,
      opacity: definition.Opacity ?? 1,
      fillColor: definition.Fill ?? definition.Color,
      fillOpacity: definition.Opacity ?? 1,
      weight: definition.Weight ?? 1,
    };
    return nativeDefinition;
  }

  getColorFromStyle = (layerStyle: MapGenericStyle) => {
    if (
      layerStyle &&
      layerStyle.styleDefinition &&
      typeof layerStyle.styleDefinition === 'object' &&
      'definition' in layerStyle.styleDefinition &&
      layerStyle.styleDefinition.definition
    ) {
      const definition = layerStyle.styleDefinition.definition;
      return definition.Fill ?? definition.Color;
    }
    return null;
  };

  setColorToStyle(style: MapCssStyle, color: string): void {
    if (!style?.definition) {
      return;
    }

    style.definition.Color = color;
    style.definition.Fill = color;
  }

  getDefaultPointStyles = (): MapCssStyle =>
    DEFAULT_LAYER_STYLES.get(LayerType.Point)?.styleDefinition as any;

  mapToDecimalOpacity = (value) => {
    let opacity = Number(value ?? 1);
    if (opacity > 1) {
      opacity = opacity / 100;
    }
    return opacity;
  };

  mapFromDecimalOpacity = (decimalValue) => {
    if (Number(decimalValue) <= 1) {
      return decimalValue * 100;
    }
    return decimalValue;
  };
}
